import React from "react";

import { MainToolbarContext } from "./MainToolbarContext";
import RowEntry from "../../types/tools/RowEntry";

import "./MainToolbar.scss";

interface MainToolbarProps {}

interface MainToolbarState {
    scroll: React.RefObject<HTMLDivElement>;
    ref: React.RefObject<HTMLDivElement>;
    observer: ResizeObserver;
    buttonIndex: number;
    isScrolling: boolean;
}

class MainToolbar extends React.Component<MainToolbarProps, MainToolbarState> {
    static contextType = MainToolbarContext;
    context!: React.ContextType<typeof MainToolbarContext>;

    state = {
        scroll: React.createRef<HTMLDivElement>(),
        ref: React.createRef<HTMLDivElement>(),
        observer: new ResizeObserver(this.didResize.bind(this)),
        buttonIndex: 0,
        isScrolling: false,
    };

    componentDidMount(): void {
        if (this.state.scroll.current)
            this.state.observer.observe(this.state.scroll.current);
    }

    componentDidUpdate(
        prevProps: Readonly<MainToolbarProps>,
        prevState: Readonly<MainToolbarState>,
        snapshot?: any,
    ): void {
        if (prevState.scroll?.current !== this.state.scroll?.current) {
            if (prevState.scroll.current)
                this.state.observer.unobserve(prevState.scroll.current);

            if (this.state.scroll.current)
                this.state.observer.observe(this.state.scroll.current);
        }
    }

    componentWillUnmount(): void {
        if (this.state.scroll.current)
            this.state.observer.unobserve(this.state.scroll.current);
        this.state.observer.disconnect();
    }

    /**
     * Bound version of the scroll test method.
     * Ensures proper 'this' context when called by event listeners.
     *
     * @method didResize
     */
    didResize() {
        const scroll = this.state.scroll.current;
        const tools = this.state.ref.current;

        if (scroll && tools) {
            const shouldScroll = scroll.offsetWidth < tools.scrollWidth;
            const scrollChanged = shouldScroll !== this.state.isScrolling;

            if (scrollChanged) {
                this.setState({ buttonIndex: 0 });

                if (scroll && scroll.scrollLeft !== 0) scroll.scrollLeft = 0;

                if (scroll) {
                    if (
                        shouldScroll &&
                        !scroll.classList.contains("need-scroll")
                    )
                        scroll.classList.add("need-scroll");
                    else if (
                        !shouldScroll &&
                        scroll.classList.contains("need-scroll")
                    )
                        scroll.classList.remove("need-scroll");
                }

                this.setState({ isScrolling: shouldScroll });
            }
        } else if (!tools && this.state.isScrolling) {
            this.setState({ isScrolling: false });
        }
    }

    /**
     * Gets all visible toolbar buttons grouped by row.
     * Filters out line breaks and hidden elements, calculates positions,
     * and groups elements by their vertical row position.
     *
     * @method getRows
     * @returns {Map<number, Array<RowEntry>>} Map of row numbers to button entries
     */
    getRows() {
        const tools = this.state.ref.current;

        if (!tools) return [];

        return Array.from(tools.children)
            .filter(
                (item: HTMLElement) =>
                    item.tagName !== "BR" && !!item.offsetParent,
            )
            .map(item => {
                const rect = item.getBoundingClientRect();
                return {
                    Row:
                        rect.top < 50
                            ? 1
                            : rect.top < 90
                            ? 2
                            : rect.top < 130
                            ? 3
                            : 4,
                    Left: rect.left,
                    Right: rect.right,
                    Element: item,
                } as RowEntry;
            })
            .groupBy(item => item.Row);
    }

    /**
     * Gets the longest row of toolbar buttons.
     * Determines which row extends furthest to the right.
     *
     * @method getLongestRow
     * @returns {Array<RowEntry>} Array of button entries in the longest row
     */
    getLongestRow() {
        const rows = this.getRows();
        let result: Array<RowEntry> = null;
        let maxLength = 0;
        for (const [, entries] of rows) {
            const max = entries.map(item => item.Right).max();
            if (max > maxLength) {
                result = entries;
                maxLength = max;
            }
        }
        return result;
    }

    /**
     * Gets the count of visible buttons in the longest row.
     *
     * @method getVisibleCount
     * @returns {number} The number of visible buttons
     */
    getVisibleCount() {
        return this.getLongestRow().length;
    }

    /**
     * Gets a toolbar button at a specific index.
     *
     * @method getTool
     * @param {number} index - The index of the button to retrieve
     * @returns {RowEntry|undefined} The button entry or undefined if index is out of bounds
     */
    getTool(index: number) {
        const children = this.getLongestRow();
        return children.length > index ? children[index] : undefined;
    }

    /**
     * Gets the right edge position of the last button.
     * Calculates the offset left plus width of the last element in the longest row.
     *
     * @method getEnd
     * @returns {number} The right edge position in pixels
     */
    getEnd() {
        const children = this.getLongestRow();
        let last = children[children.length - 1];
        return last.Element.offsetLeft + last.Element.offsetWidth + 20;
    }

    /**
     * Checks if there is overflow requiring scroll navigation.
     * Determines if the end of the toolbar extends beyond the visible area.
     *
     * @method getDifference
     * @returns {boolean} True if scrolling is needed, false otherwise
     */
    getDifference() {
        const scroll = this.state.scroll.current;
        const tools = this.state.ref.current;

        if (!scroll || !tools) return false;

        const left = parseInt(tools.style.left);
        return this.getEnd() + (isNaN(left) ? 0 : left) > scroll.offsetWidth;
    }

    /**
     * Internal handler for previous button clicks.
     * Scrolls the toolbar to show the previous button.
     *
     * @method prev
     */
    prev() {
        const scroll = this.state.scroll.current;
        let buttonIndex = this.state.buttonIndex;

        if (scroll.scrollLeft > 0) {
            while (
                this.getTool(buttonIndex).Element.offsetLeft < scroll.scrollLeft
            )
                buttonIndex++;

            do {
                buttonIndex--;
            } while (
                buttonIndex > 0 &&
                this.getTool(buttonIndex).Element.offsetLeft > scroll.scrollLeft
            );

            scroll.scrollTo({
                left: this.getTool(buttonIndex).Element.offsetLeft,
                behavior: "smooth",
            });

            this.setState({ buttonIndex });
        }
    }

    /**
     * Internal handler for next button clicks.
     * Scrolls the toolbar to show the next button if overflow exists.
     *
     * @method next
     */
    next() {
        const scroll = this.state.scroll.current;
        let buttonIndex = this.state.buttonIndex;

        if (scroll.scrollLeft + scroll.offsetWidth < this.getEnd() + 20) {
            while (
                this.getTool(buttonIndex).Element.offsetLeft > scroll.scrollLeft
            )
                buttonIndex--;

            do {
                buttonIndex++;
            } while (
                buttonIndex < this.getVisibleCount() &&
                this.getTool(buttonIndex).Element.offsetLeft < scroll.scrollLeft
            );

            if (this.getDifference())
                scroll.scrollTo({
                    left: this.getTool(buttonIndex).Element.offsetLeft,
                    behavior: "smooth",
                });
            else buttonIndex--;
        }

        this.setState({ buttonIndex });
    }

    render(): React.ReactNode {
        this.context.ref = this.state.ref;

        return (
            <div className="main-toolbar">
                <div className="custom-tools">
                    <div
                        ref={this.state.scroll}
                        className={`tool-scroll${
                            this.state.isScrolling ? " need-scroll" : ""
                        }`}>
                        <div
                            className="prev"
                            onClick={this.prev.bind(this)}></div>
                        <div
                            className="next"
                            onClick={this.next.bind(this)}></div>
                        <div
                            ref={this.state.ref}
                            className="tools"></div>
                    </div>
                </div>
            </div>
        );
    }
}

export default MainToolbar;
